import { c as _c } from "react/compiler-runtime";
import { QUICK_FILTER } from "@hooks/table-filters/use-filter-query-params";
import { useMarkupsViewQueryParams } from "@pages/project-details/project-markups/calendar/use-markups-view-query-params";
import { ProjectMarkupsTableHeaders } from "@pages/project-details/project-markups/project-markups-table-utils";
import { useAppSelector } from "@store/store-helper";
import { currentUserSelector } from "@store/user/user-selector";
import { AnnotationEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
/**
 * Returns the props of the event tracked when the annotations are loaded: the view and the filters.
 * Assignees, creators and tags are counted rather than listed, as their values identify users and customer data.
 */
export function getMarkupsLoadedEventProps({
  searchParams,
  viewMode,
  calendarView,
  currentUserId,
  totalCount,
  shownCount
}) {
  const assigneeIds = searchParams.getAll(ProjectMarkupsTableHeaders.assignee);
  const creatorIds = searchParams.getAll(ProjectMarkupsTableHeaders.creator);
  return {
    viewMode,
    ...(viewMode === "calendar" && {
      calendarView
    }),
    filteredBy: Object.values(ProjectMarkupsTableHeaders).filter(column => searchParams.has(column)),
    statuses: searchParams.getAll(ProjectMarkupsTableHeaders.status),
    dueDates: searchParams.getAll(ProjectMarkupsTableHeaders.dueDate),
    attachments: searchParams.getAll(ProjectMarkupsTableHeaders.attachments),
    assigneeFilterCount: assigneeIds.length,
    isAssignedToMe: !!currentUserId && assigneeIds.length === 1 && assigneeIds[0] === currentUserId,
    creatorFilterCount: creatorIds.length,
    isCreatedByMe: !!currentUserId && creatorIds.length === 1 && creatorIds[0] === currentUserId,
    tagFilterCount: searchParams.getAll(ProjectMarkupsTableHeaders.tags).length,
    hasQuickFilter: !!searchParams.get(QUICK_FILTER),
    totalCount,
    shownCount
  };
}
/** Tracks once, when the annotations are loaded, the view and the filters the tab was opened with */
export function useTrackMarkupsLoaded(t0) {
  const $ = _c(18);
  const {
    isLoading,
    totalCount,
    shownCount
  } = t0;
  const [searchParams] = useSearchParams();
  const {
    viewMode,
    calendarView
  } = useMarkupsViewQueryParams();
  const currentUser = useAppSelector(currentUserSelector);
  const {
    trackEvent
  } = useTrackEvent();
  const hasTrackedEvent = useRef(false);
  let t1;
  if ($[0] !== calendarView || $[1] !== currentUser?.identity || $[2] !== isLoading || $[3] !== searchParams || $[4] !== shownCount || $[5] !== totalCount || $[6] !== trackEvent || $[7] !== viewMode) {
    t1 = () => {
      if (isLoading || hasTrackedEvent.current) {
        return;
      }
      hasTrackedEvent.current = true;
      trackEvent({
        name: AnnotationEvents.loadedAnnotations,
        props: getMarkupsLoadedEventProps({
          searchParams,
          viewMode,
          calendarView,
          currentUserId: currentUser?.identity,
          totalCount,
          shownCount
        })
      });
    };
    $[0] = calendarView;
    $[1] = currentUser?.identity;
    $[2] = isLoading;
    $[3] = searchParams;
    $[4] = shownCount;
    $[5] = totalCount;
    $[6] = trackEvent;
    $[7] = viewMode;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] !== calendarView || $[10] !== currentUser || $[11] !== isLoading || $[12] !== searchParams || $[13] !== shownCount || $[14] !== totalCount || $[15] !== trackEvent || $[16] !== viewMode) {
    t2 = [isLoading, searchParams, viewMode, calendarView, currentUser, totalCount, shownCount, trackEvent];
    $[9] = calendarView;
    $[10] = currentUser;
    $[11] = isLoading;
    $[12] = searchParams;
    $[13] = shownCount;
    $[14] = totalCount;
    $[15] = trackEvent;
    $[16] = viewMode;
    $[17] = t2;
  } else {
    t2 = $[17];
  }
  useEffect(t1, t2);
}